import { useCallback, useEffect, useState } from "react";
import {
  Banner,
  BlockStack,
  Button,
  Card,
  InlineStack,
  Select,
  Text,
} from "@shopify/polaris";
import type { QueueAccount } from "../../../../packages/shared/shopify-queue/client";
import { stockScheduleRequest } from "../../../../packages/shared/stock-schedule/client";
import {
  StockScheduleControls,
  type StockScheduleData,
} from "./StockScheduleControls";
import { PublicationDetail } from "../stock-publication/PublicationHistory";
type Data = StockScheduleData & {
  stores: {id: string; domain: string; connected: number}[];
  selected_connection_id: string | null;
  expiry?:{last_day:string|null;target_day:string|null;checked_at:string;error:string|null;queued:number}|null;
  runs: {
    id: string;
    domain: string;
    created_at: string;
    product_count: number;
    new_job_count: number;
    waiting: number;
    confirmed: number;
    needs_review: number;
  }[];
  total: number;
  nextCursor: number | null;
};
const base = import.meta.env.VITE_API_URL || "https://api.whenlyapp.com";
export function StockSchedulePanel({
  account,
  canSchedule,
}: {
  account: QueueAccount;
  canSchedule: boolean;
}) {
  const [data, setData] = useState<Data | null>(null),
    [error, setError] = useState(""),
    [pages, setPages] = useState<number[]>([]),
    [refresh, setRefresh] = useState(0),
    [connectionId, setConnectionId] = useState(""),
    [selected, setSelected] = useState<string | null>(null),
    cursor = pages[pages.length - 1];
  const reload = useCallback(() => {
    setPages([]);
    setRefresh((v) => v + 1);
  }, []);
  useEffect(() => {
    const abort = new AbortController();
    setData(null);
    setError("");
    void stockScheduleRequest<Data>(
      base,
      account,
      "?" + new URLSearchParams({...connectionId ? {connection_id: connectionId} : {}, ...cursor ? {cursor: String(cursor)} : {}}),
      { signal: abort.signal },
    )
      .then((v) => {
        if (!abort.signal.aborted) setData(v);
      })
      .catch((e) => {
        if (!abort.signal.aborted) setError(e.message);
      });
    return () => abort.abort();
  }, [account, cursor, refresh, connectionId]);
  return (
    <BlockStack gap="400">
      <InlineStack gap="200">
        <Button onClick={reload}>Refresh stock schedule</Button>
        <Button url="/shopify-updates">
          Manual publication and failed update retries
        </Button>
      </InlineStack>
      {error && <Banner tone="critical">{error}</Banner>}
      {!data && !error && <Text as="p">Loading stock schedule…</Text>}
      {data && (
        <>
          <Select label="Schedule store" value={data.selected_connection_id || ""}
            options={[{label: "Choose a store; show all runs", value: ""}, ...data.stores.map(s => ({label: s.domain + (s.connected ? "" : " (disconnected)"), value: s.id}))]}
            onChange={id => {setConnectionId(id); setPages([]); setSelected(null);}} />
          <StockScheduleControls
            key={data.selected_connection_id || "none"}
            account={account}
            data={data}
            canSchedule={canSchedule}
            onSaved={reload}
          />
          <Card><BlockStack gap="200"><Text as="h2" variant="headingMd">Automatic expiry checks</Text>
            <Text as="p">Expiry uses the company time zone. Free expired goods and unusable bins are excluded from sale. Physical stock stays unchanged.</Text>
            {data.expiry ? <><Text as="p">Last complete check: {data.expiry.last_day||'Not completed'}. Products queued in this check: {data.expiry.queued}. Last checked {data.expiry.checked_at} UTC.</Text>
              {data.expiry.target_day&&<Text as="p">The check for {data.expiry.target_day} is still working through products.</Text>}
              {data.expiry.error&&<Banner tone="warning">{data.expiry.error}</Banner>}
            </> : <Text as="p">No expiry check has run for this company. Checks begin when dated goods and a connected store are present.</Text>}
            <Text as="p">Queued products still need a confirmed Shopify update. Review their results on the stock updates page.</Text>
          </BlockStack></Card>
          <Card>
            <BlockStack gap="300">
              <Text as="h2" variant="headingMd">
                Scheduled stock runs
              </Text>
              <Text as="p">
                {data.total} saved runs. Results reflect the current state of
                their exact saved product updates.
              </Text>
              {data.runs.map((run) => (
                <BlockStack key={run.id} gap="200">
                  <Text as="h3" variant="headingSm">
                    {run.created_at} UTC · {run.domain}
                  </Text>
                  <Text as="p">
                    {run.product_count} products queued. Confirmed:{" "}
                    {run.confirmed}. Waiting or sending: {run.waiting}. Need
                    review: {run.needs_review}.
                  </Text>
                  <Button
                    onClick={() =>
                      setSelected(selected === run.id ? null : run.id)
                    }
                  >
                    View run {run.id}
                  </Button>
                  {selected === run.id && (
                    <PublicationDetail
                      key={run.id + refresh}
                      id={run.id}
                      account={account}
                    />
                  )}
                </BlockStack>
              ))}
              {!data.runs.length && (
                <Text as="p">No scheduled stock publications have run.</Text>
              )}
              <InlineStack gap="200">
                {!!pages.length && (
                  <Button onClick={() => setPages((p) => p.slice(0, -1))}>
                    Newer runs
                  </Button>
                )}
                {data.nextCursor && (
                  <Button
                    onClick={() => setPages((p) => [...p, data.nextCursor!])}
                  >
                    Older runs
                  </Button>
                )}
              </InlineStack>
            </BlockStack>
          </Card>
        </>
      )}
    </BlockStack>
  );
}
